import ApiModel                          from '@widesk-core/models/ApiModel';
import { observer }                      from 'mobx-react-lite';
import { reaction }                      from 'mobx';
import React                             from 'react';
import FunctionalityStore                from './FunctionalityStore';
import Crown                             from './Crown';
import PendingMark                       from './PendingMark';
import GateFallback                      from './GateFallback';
import { BlockedFunctionality }          from './types';
import { CommonGateProps }               from './types';
import { FunctionalityConfig }           from './types';
import { FunctionalityEntry }            from './types';
import { FunctionalityGateProps }        from './types';
import { FunctionalityLockMarkProps }    from './types';
import { FunctionalityStoreOf }          from './types';

const DEFAULT_FEATURE_MESSAGE = 'Fonctionnalité non disponible dans votre abonnement';

const negate = (value?: boolean) => (value === undefined ? undefined : !value);

/**
 * Instancie le moteur des fonctionnalités pour un projet : retourne le composant
 * `FunctionalityGate` et le store, tous deux typés par le dictionnaire fourni
 * (référence autocomplétée, contexte exigé/interdit/typé selon le scope de l'entrée).
 *
 * Les composants et le store travaillent en interne avec des types relâchés
 * (voir FunctionalityStore) : les signatures précises sont posées ici, à la frontière.
 */
export default function createFunctionality<D extends Record<string, FunctionalityEntry>>(config: FunctionalityConfig<D>) {
	const store = new FunctionalityStore(config);

	type LooseGateProps = CommonGateProps & { reference: string; context?: ApiModel };

	const FunctionalityGate = (props: LooseGateProps) => {
		if (config.functionalities[props.reference]?.type === 'limit') {
			return <FunctionalityLimitGate {...props} />;
		}

		return <FunctionalityFeatureGate {...props} />;
	};

	const renderDetailsOf = (blocked: BlockedFunctionality) => {
		const { renderBlockedDetails } = config;

		return renderBlockedDetails ? (close: () => void) => renderBlockedDetails(blocked, close) : undefined;
	};

	// Le store met comptages et limites en cache : un gate qui (re)apparaît doit revoir la réalité
	// (ex : un projet ajouté depuis un autre écran, une limite d'abonnement modifiée).
	const useFreshUsage = (query: { reference: string; context?: ApiModel }) => React.useEffect(() => {
		store.refreshSubscriptionValue(query.reference);

		return reaction(
			() => store.canCount(query),
			canCount => canCount && store.refreshUsage(query),
			{ fireImmediately: true },
		);
	}, [query.reference, query.context?.id]);

	const FunctionalityLimitGate = observer((props: LooseGateProps) => {
		const { reference, fallback, message, fullWidth, children } = props;

		useFreshUsage(props);

		const usage = store.usage(props);
		const limit = store.limit(reference);
		const loading = usage === undefined || limit === undefined;

		if (!loading && usage < limit) return <>{children}</>;

		const disabledMessage = `Limite atteinte (${usage ?? '…'}/${limit ?? '…'})`;

		return (
			<GateFallback
				fallback={fallback}
				message={message ?? disabledMessage}
				fullWidth={fullWidth}
				loading={loading}
				renderDetails={renderDetailsOf({ limit, reference, scope: store.scopeOf(reference), usage })}
			>
				{children}
			</GateFallback>
		);
	});

	const FunctionalityFeatureGate = observer((props: LooseGateProps) => {
		const { reference, fallback, message = DEFAULT_FEATURE_MESSAGE, fullWidth, children } = props;

		// L'activation d'une feature peut changer en cours de session (abonnement modifié) :
		// on revérifie à chaque (ré)apparition du gate.
		React.useEffect(() => store.refreshSubscriptionValue(reference), [reference]);

		const enabled = store.isAvailable(reference);

		if (enabled) return <>{children}</>;

		return (
			<GateFallback
				fallback={fallback}
				message={message}
				fullWidth={fullWidth}
				loading={enabled === undefined}
				renderDetails={renderDetailsOf({ reference })}
			>
				{children}
			</GateFallback>
		);
	});

	const FunctionalityLimitLockMark = observer((props: { reference: string; context?: ApiModel }) => {
		useFreshUsage(props);

		const isBlocked = store.isLimitReached(props);

		if (isBlocked === undefined) return <PendingMark />;

		return isBlocked ? <Crown /> : null;
	});

	const FunctionalityFeatureLockMark = observer(({ reference }: { reference: string }) => {
		React.useEffect(() => store.refreshSubscriptionValue(reference), [reference]);

		const isBlocked = negate(store.isAvailable(reference));

		if (isBlocked === undefined) return <PendingMark />;

		return isBlocked ? <Crown /> : null;
	});

	const FunctionalityLockMark = (props: { reference: string; context?: ApiModel }) => (
		config.functionalities[props.reference]?.type === 'limit'
			? <FunctionalityLimitLockMark {...props} />
			: <FunctionalityFeatureLockMark reference={props.reference} />
	);

	return {
		FunctionalityGate: FunctionalityGate as (props: FunctionalityGateProps<D>) => React.ReactNode,
		FunctionalityLockMark: FunctionalityLockMark as (props: FunctionalityLockMarkProps<D>) => React.ReactNode,
		functionalityStore: store as FunctionalityStoreOf<D>,
	};
}
